import { Button, Col, Divider, Form, Row, Select, Skeleton, Input } from "antd";
import { reconcileColumns } from "features/appearance/components/tableAppearanceModal";
import { SIDER_ITEMS } from "features/appearance/constants";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useAppearance } from "providers/appearanceProvider";
import styled from "styled-components";
import { debounce } from "common/utils";
import { useFetchCreditorSummariesQuery } from "features/creditors/agencyPortal/creditorsAPI";
import { useFetchDisputeCategoriesQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import {
  AVAILABLE_DATA_SOURCES,
  AVAILABLE_STATUSES,
} from "features/disputeManagementTable/constants";

const StyledFilterHeader = styled.h4`
  font-weight: 600;
  color: #1677ff;
`;

const StyledSubHeader = styled.h5`
  margin-top: 0;
`;

const StyledRow = styled(Row)`
  align-items: baseline;
  padding: 12px;
  border-left: 1px solid #f0f0f0;
  border-top: 1px solid #f0f0f0;
  background-color: #fff;
  min-width: 250px;
`;

const StyledDivider = styled(Divider)`
  margin-top: 0;
`;

const StyledContainer = styled.div`
  height: calc(100vh - 250px);
  overflow: scroll;
  padding: 12px;
  border-left: 1px solid #f0f0f0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fff;
  margin-bottom: 12px;
  min-width: 250px;
`;

const HAS_DOC_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

export const SIDER_ID = "disputeFilters";

function DisputeManagementTableFilters({ filters, setFilters = (args) => undefined }) {
  const [form] = Form.useForm();
  const { data: creditors, isLoading: isCreditorLoading } = useFetchCreditorSummariesQuery();
  const { data: categories, isLoading: isCategoriesLoading } = useFetchDisputeCategoriesQuery();
  const { isLoading: isAppearanceLoading, appearance } = useAppearance();
  const {
    data: { [PERMISSIONS.APPEARANCE__UPDATE]: isAppearanceUpdateAuthorized },
  } = useAuthorizations();

  const initialValues = filters;

  const onIncludeClientNamesChange = async (value) => {
    setFilters((prev) => ({
      ...prev,
      includeCreditorIds: value && value.length > 0 ? value : undefined,
    }));
  };

  const onExcludeClientNamesChange = async (value) => {
    setFilters((prev) => ({
      ...prev,
      excludeCreditorIds: value && value.length > 0 ? value : undefined,
    }));
  };

  const onIncludeStatusesChange = async (values) => {
    setFilters((prev) => ({
      ...prev,
      includeStatuses: values && values.length > 0 ? values : undefined,
    }));
  };

  const onExcludeStatusesChange = async (values) => {
    setFilters((prev) => ({
      ...prev,
      excludeStatuses: values && values.length > 0 ? values : undefined,
    }));
  };

  const onIncludeDataSourcesChange = async (values) => {
    setFilters((prev) => ({
      ...prev,
      includeDataSources: values && values.length > 0 ? values : undefined,
    }));
  };

  const onExcludeDataSourcesChange = async (values) => {
    setFilters((prev) => ({
      ...prev,
      excludeDataSources: values && values.length > 0 ? values : undefined,
    }));
  };

  const onIncludeCategoryTagIdsChange = async (values) => {
    setFilters((prev) => ({
      ...prev,
      includeCategoryTagIds: values && values.length > 0 ? values : undefined,
    }));
  };

  const onExcludeCategoryTagIdsChange = async (values) => {
    setFilters((prev) => ({
      ...prev,
      excludeCategoryTagIds: values && values.length > 0 ? values : undefined,
    }));
  };

  const onDebtorNameChange = debounce(async (value) => {
    setFilters((prev) => ({
      ...prev,
      debtorName: value && value.length > 0 ? value : undefined,
    }));
  }, 500);

  const onHasDocumentsChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      hasDocuments: value,
    }));
  };

  const onResetClicked = async () => {
    setFilters({});
    form.resetFields();
  };

  const defaultItems = [
    {
      key: SIDER_ITEMS.DISPUTE_FILTERS__STATUSES,
      visible: true,
      title: "Statuses",
      render: (item) => (
        <span key={item.key}>
          <StyledFilterHeader>{item.newTitle ?? item.originalTitle}</StyledFilterHeader>
          <Form.Item name="includeStatuses">
            <StyledSubHeader>Include</StyledSubHeader>
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              placeholder="Select Status..."
              onChange={onIncludeStatusesChange}
              options={AVAILABLE_STATUSES.map(({ text, value }) => ({
                label: text,
                value,
              }))}
              allowClear
            />
          </Form.Item>
          <Form.Item name="excludeStatuses">
            <StyledSubHeader>Exclude</StyledSubHeader>
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              placeholder="Select Status..."
              onChange={onExcludeStatusesChange}
              options={AVAILABLE_STATUSES.map(({ text, value }) => ({
                label: text,
                value,
              }))}
              allowClear
            />
          </Form.Item>
          <StyledDivider />
        </span>
      ),
    },
    {
      key: SIDER_ITEMS.DISPUTE_FILTERS__CATEGORY_TAG_IDS,
      visible: true,
      title: "Categories",
      render: (item) => (
        <span key={item.key}>
          <StyledFilterHeader>{item.newTitle ?? item.originalTitle}</StyledFilterHeader>
          <Form.Item name="includeCategoryTagIds">
            <StyledSubHeader>Include</StyledSubHeader>
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              placeholder="Select Category..."
              onChange={onIncludeCategoryTagIdsChange}
              options={categories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              }))}
              allowClear
            />
          </Form.Item>
          <Form.Item name="excludeCategoryTagIds">
            <StyledSubHeader>Exclude</StyledSubHeader>
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              placeholder="Select Category..."
              onChange={onExcludeCategoryTagIdsChange}
              options={categories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              }))}
              allowClear
            />
          </Form.Item>
          <StyledDivider />
        </span>
      ),
    },
    {
      key: SIDER_ITEMS.DISPUTE_FILTERS__CLIENT_NAMES,
      visible: true,
      title: "Clients",
      render: (item) => (
        <span key={item.key}>
          <StyledFilterHeader>{item.newTitle ?? item.originalTitle}</StyledFilterHeader>
          <Form.Item name="includeClientNames">
            <StyledSubHeader>Include</StyledSubHeader>
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              placeholder="Select Client..."
              optionFilterProp="label"
              showSearch
              onChange={onIncludeClientNamesChange}
              options={creditors.map((creditor) => ({
                value: creditor.id,
                label: creditor.name,
              }))}
              allowClear
            />
          </Form.Item>
          <Form.Item name="includeClientNames">
            <StyledSubHeader>Exclude</StyledSubHeader>
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              placeholder="Select Client..."
              optionFilterProp="label"
              showSearch
              onChange={onExcludeClientNamesChange}
              options={creditors.map((creditor) => ({
                value: creditor.id,
                label: creditor.name,
              }))}
              allowClear
            />
          </Form.Item>
          <StyledDivider />
        </span>
      ),
    },
    {
      key: SIDER_ITEMS.DISPUTE_FILTERS__DATA_SOURCES,
      visible: true,
      title: "Data Sources",
      render: (item) => (
        <span key={item.key}>
          <StyledFilterHeader>{item.newTitle ?? item.originalTitle}</StyledFilterHeader>
          <Form.Item name="includeDataSources">
            <StyledSubHeader>Include</StyledSubHeader>
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              placeholder="Select Data Source..."
              onChange={onIncludeDataSourcesChange}
              options={AVAILABLE_DATA_SOURCES.map(({ text, value }) => ({
                label: text,
                value,
              }))}
              allowClear
            />
          </Form.Item>
          <Form.Item name="excludeDataSources">
            <StyledSubHeader>Exclude</StyledSubHeader>
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              placeholder="Select Data Source..."
              onChange={onExcludeDataSourcesChange}
              options={AVAILABLE_DATA_SOURCES.map(({ text, value }) => ({
                label: text,
                value,
              }))}
              allowClear
            />
          </Form.Item>
          <StyledDivider />
        </span>
      ),
    },
    {
      key: SIDER_ITEMS.DISPUTE_FILTERS__DEBTOR_FIRST_NAME,
      visible: true,
      title: "Debtor Name",
      render: (item) => (
        <span key={item.key}>
          <StyledFilterHeader>{item.newTitle ?? item.originalTitle}</StyledFilterHeader>
          <Form.Item name="debtorName">
            <Input
              placeholder="Debtor Name"
              value={filters.debtorName || ""}
              onChange={(e) => onDebtorNameChange(e.target.value)}
              allowClear
            />
          </Form.Item>
          <Divider />
        </span>
      ),
    },
    {
      key: SIDER_ITEMS.DISPUTE_FILTERS__HAS_DOCUMENTS,
      visible: true,
      title: "Has Evidence Of Debt",
      render: (item) => {
        // Find the option whose value matches filters.hasDocuments (or fallback to “All”)
        const currentOption = HAS_DOC_OPTIONS.find((opt) => opt.value === filters.hasDocuments) || {
          label: "",
          value: undefined,
        };

        return (
          <span key={item.key}>
            <StyledFilterHeader>{item.newTitle ?? item.originalTitle}</StyledFilterHeader>

            <Form.Item name="hasDocuments">
              <Select
                popupMatchSelectWidth={false}
                value={currentOption.value}
                onChange={(val) => {
                  onHasDocumentsChange(val);
                }}
                placeholder="Should have debt evidence"
                options={HAS_DOC_OPTIONS}
                allowClear
              />
            </Form.Item>

            <Divider />
          </span>
        );
      },
    },
  ];

  const reconciledItems = reconcileColumns(defaultItems, appearance?.items?.[SIDER_ID]).filter(
    (column) => column.visible,
  );
  if (
    appearance?.items?.[SIDER_ID]?.hidePanel ||
    isAppearanceLoading ||
    isCreditorLoading ||
    isCategoriesLoading
  ) {
    return null;
  }

  return (
    <Col>
      <StyledRow justify="space-between">
        <h3>Filters</h3>
        <Button type="link" onClick={onResetClicked}>
          Reset
        </Button>
      </StyledRow>
      <StyledContainer>
        {isAppearanceLoading ? (
          <Skeleton active />
        ) : (
          <Form
            form={form}
            labelAlign="right"
            initialValues={initialValues}
            validateMessages={{ required: "This is a required field" }}
            layout="vertical"
          >
            {reconciledItems.map((item) => item.render(item))}
          </Form>
        )}
      </StyledContainer>
    </Col>
  );
}

export default DisputeManagementTableFilters;
